import type { World } from '../ecs/world'
import type { ArmamentId } from '../ecs/entity'
import type { InputSource } from '../input/types'
import { Tile } from './tiles'
import { makeBoss } from '../entities/bosses'
import { makeEnemy, makeGenerator, makePickup, makePlayer, tileCenter } from '../entities/templates'
import type { LevelData } from './level'

export interface CarriedPlayerStats {
  hp: number
  magic: number
}

/**
 * Instantiate a LevelData into the world. Works identically for authored and
 * procedural levels — the schema is the contract. One source per player slot
 * (co-op-ready: pass two sources, get two participants).
 */
export function loadLevel(
  world: World,
  level: LevelData,
  floor: number,
  sources: InputSource[],
  carried?: (CarriedPlayerStats | undefined)[]
): void {
  world.beginLevel(level, floor)

  sources.forEach((source, i) => {
    const spawn = level.playerSpawns[i % level.playerSpawns.length]!
    const p = makePlayer(world.tuning, source, spawn.x, spawn.y)
    // requisitioned chassis ranks raise the cap for the whole run
    p.maxHp = world.tuning.player.maxHp + world.mods.maxHpBonus
    const c = carried?.[i]
    p.hp = c
      ? Math.min(p.maxHp, Math.max(20, c.hp)) // never spawn into a floor already dying
      : p.maxHp
    if (c) p.player!.magic = c.magic
    world.add(p)
    world.bus.emit('player_spawn', { levelId: level.id })
  })

  for (const g of level.generators) {
    world.add(
      makeGenerator(world.tuning, g.type, g.x, g.y, level.band, {
        ...(g.budget !== undefined ? { budget: g.budget } : {}),
        ...(g.interval !== undefined ? { interval: g.interval } : {})
      })
    )
  }
  for (const e of level.enemies) {
    world.add(makeEnemy(world.tuning, e.type, tileCenter(e.x), tileCenter(e.y), undefined, level.band))
  }
  for (const pk of level.pickups) {
    world.add(makePickup(pk.type, pk.x, pk.y))
  }
  if (level.boss) {
    world.add(makeBoss(world.tuning, level.boss.id, level.boss.x, level.boss.y))
  }

  // DECISION: armament floor-issue is rolled at load time from the level RNG
  // rather than authored into the frozen schema — standard floors only, placed
  // on the floor tile farthest from spawn (the "supply crate in a far room"
  // feel without a schema change). Assembler drops are the other source.
  if (!level.isBossGate && world.levelRng.chance(world.tuning.armaments.floorIssueChance)) {
    const spawn = level.playerSpawns[0]!
    let best: { x: number; y: number } | null = null
    let bestD = -1
    for (let i = 0; i < 80; i++) {
      const x = world.levelRng.int(1, world.map.w - 2)
      const y = world.levelRng.int(1, world.map.h - 2)
      if (world.map.at(x, y) !== Tile.Floor) continue
      const d = (x - spawn.x) ** 2 + (y - spawn.y) ** 2
      if (d > bestD) {
        bestD = d
        best = { x, y }
      }
    }
    if (best) {
      const ids: ArmamentId[] = ['scatter', 'lance', 'seam', 'suppressor']
      world.add(makePickup('armament', best.x, best.y, ids[world.levelRng.int(0, ids.length - 1)]))
    }
  }
  world.sweep()
}
